import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { SttSessionsResponse } from '@utils/types/stt-sessions/stt-sessions.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';

/** Statuses a session can still move out of on its own. */
const IN_FLIGHT_STATUSES = new Set([
  'new',
  'uploading',
  'uploaded',
  'queued',
  'downloading',
  'transcoded',
  'processing',
  'streaming'
]);

/** Poll only while something on the page can still change. */
const POLLING_INTERVAL_MS = 15 * 1000;

const hasInFlightSession = (data?: SttSessionsResponse) =>
  (data?.result ?? []).some(session => IN_FLIGHT_STATUSES.has(session.status));

export const useGetSttSessions = () => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  const fetchSttSessions = async () => {
    const { sttSessions } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });
    const { data } = await Axios.get<SttSessionsResponse>(sttSessions);
    return data;
  };

  return useQuery<SttSessionsResponse, AxiosError<BaseError>>(
    ['sttSessions', offset, limit, qString, sortField, sortDirection],
    fetchSttSessions,
    {
      // Sessions move through new -> queued -> processing -> finished on the
      // server's schedule, so a five minute staleTime with every refetch
      // trigger disabled meant the list could sit on a stale snapshot
      // indefinitely: a session already queued still showed as "Uploaded".
      staleTime: 15 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      // Same conditional-polling shape as use-get-transcript-session: refresh
      // while anything is still in flight, then stop. A page of finished
      // sessions costs no requests.
      refetchInterval: data =>
        hasInFlightSession(data) ? POLLING_INTERVAL_MS : false,
      keepPreviousData: true,
      useErrorBoundary: false,
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
